/**
 * Load public runtime config into window.__SANCTUARY_ENV before auth boots.
 *
 * Hosted web gets this from Cloudflare Pages `/runtime-env`. The Electron
 * bundle ships a static stub, so the desktop shell must fetch the live
 * values (or use a build-time baked file) before AuthProvider selects
 * WorkOS vs local-dev.
 */

import { getApiOrigin } from './api-base';

type SanctuaryRuntimeEnv = {
  WORKOS_CLIENT_ID?: string;
  WORKOS_API_HOSTNAME?: string;
  WORKOS_DEV_MODE?: string;
  DISABLE_WORKOS_AUTH?: string;
  SUPABASE_URL?: string;
  SUPABASE_ANON_KEY?: string;
  ASSETS_URL?: string;
};

declare global {
  interface Window {
    __SANCTUARY_ENV?: SanctuaryRuntimeEnv;
    WORKOS_CLIENT_ID?: string;
    WORKOS_API_HOSTNAME?: string;
    SUPABASE_URL?: string;
    SUPABASE_ANON_KEY?: string;
    ASSETS_URL?: string;
  }
}

const DESKTOP_PROTOCOLS = new Set(['file:', 'app:', 'sanctuary-app:']);

function isDesktopShellOrigin(): boolean {
  return typeof window !== 'undefined' && DESKTOP_PROTOCOLS.has(window.location.protocol);
}

function applyRuntimeEnvScript(source: string): boolean {
  const trimmed = source.trim();
  if (!trimmed || !trimmed.includes('__SANCTUARY_ENV')) return false;

  // The hosted endpoint returns assignment statements, not a module.
  // eslint-disable-next-line no-new-func
  new Function(trimmed)();

  const clientId = window.__SANCTUARY_ENV?.WORKOS_CLIENT_ID?.trim();
  return Boolean(clientId);
}

/**
 * Ensure WorkOS (and related public) config is available before React mounts.
 * No-op when `/runtime-env` already populated window.__SANCTUARY_ENV (hosted
 * web, or Electron with a baked runtime-env file).
 */
export async function ensureRuntimeEnvLoaded(): Promise<void> {
  if (typeof window === 'undefined') return;

  const existing = window.__SANCTUARY_ENV?.WORKOS_CLIENT_ID?.trim();
  if (existing) return;

  const origin = isDesktopShellOrigin() ? getApiOrigin() : '';
  // Prefer same-origin first (baked Electron file or Pages function), then hosted.
  const urls = [
    '/runtime-env',
    '/runtime-env.js',
    ...(origin ? [`${origin}/runtime-env`, `${origin}/runtime-env.js`] : []),
  ];

  for (const url of urls) {
    try {
      const response = await fetch(url, { cache: 'no-store' });
      if (!response.ok) continue;
      const source = await response.text();
      if (applyRuntimeEnvScript(source)) {
        console.info('[runtime-env] Loaded WorkOS public config from', url);
        return;
      }
    } catch (error) {
      console.warn(`[runtime-env] Failed to load ${url}:`, error);
    }
  }

  if (isDesktopShellOrigin()) {
    console.error(
      '[runtime-env] WorkOS client ID unavailable. Desktop login will not work until config loads.',
    );
  }
}
